import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LoyaltyTransactionResponse, LoyaltyTransaction } from '../../models/loyalty-program';
import { CircleButtonComponent } from '../buttons/circle-button.component';
import { BaseButtonComponent } from '../buttons/base-button.component';
import { SeparatorComponent } from './separator.component';
import { LoadingSpinnerComponent } from './loading-spinner-component';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthorizedApiService } from '../../services/authorized-api.service';
import {catchError, finalize, map, Observable, of, startWith, switchMap, take} from 'rxjs';
import { AppStateService } from '../../services/app-state.service';
import {UrlHistoryService} from '../../services/url.history.service';

@Component({
  selector: 'app-loyalty-card-history',
  standalone: true,
  imports: [
    CommonModule,
    CircleButtonComponent,
    BaseButtonComponent,
    SeparatorComponent,
    LoadingSpinnerComponent
  ],
  template: `
    <div class="max-w-lg mx-auto flex-col h-screen pt-20 min-h-[calc(100vh)]">
      <div class="flex-1 bg-theme-surface rounded-t-3xl min-h-full">
        <!-- Header -->
        <div class="flex flex-row pt-6 px-4 pb-4 items-center">
          <div class="mr-3">
            <app-circle-button [icon]="'fa-chevron-left'" size="12" type="solid" fontSize="xl" (onClick)="goBack()"></app-circle-button>
          </div>
          <div class="ml-2 text-3xl px-4 break-words text-theme-text">Historik</div>
        </div>

        <!-- Loading state -->
        <div *ngIf="isLoading" class="px-8 py-6 flex justify-center">
          <app-loading-spinner size="lg"></app-loading-spinner>
        </div>

        <!-- Card information and summary -->
        <div *ngIf="!isLoading && data" class="px-8 py-4 text-theme-text">
          <div class="mb-6">
            <div class="flex justify-between mb-2">
              <div class="font-semibold">Kortnummer</div>
              <div>{{ data.card.identity }}</div>
            </div>
            <div class="flex justify-between mb-2">
              <div class="font-semibold">Namn</div>
              <div>{{ data.card.first_name }} {{ data.card.last_name }}</div>
            </div>
            <div class="flex justify-between mb-2">
              <div class="font-semibold">Nuvarande saldo</div>
              <div class="text-theme-primary font-bold tabular-nums">{{ data.current_balance }} poäng</div>
            </div>
          </div>

          <!-- Transaction summary -->
          <div class="bg-theme-surface2 rounded-lg p-4 mb-6">
            <div class="grid grid-cols-2 gap-4">
              <div class="text-center">
                <div class="text-xl font-bold text-theme-primary tabular-nums">{{ data.total_points_earned }}</div>
                <div class="text-sm">Intjänade poäng</div>
              </div>
              <div class="text-center">
                <div class="text-xl font-bold text-theme-primary tabular-nums">{{ data.total_points_redeemed }}</div>
                <div class="text-sm">Inlösta poäng</div>
              </div>
            </div>
          </div>

          <!-- Transactions list title -->
          <div class="font-bold text-lg mb-3">Transaktioner</div>

          <!-- Transactions list -->
          <div *ngIf="data.transactions.length > 0" class="mb-6">
            <div *ngFor="let transaction of data.transactions; let last=last" class="">
              <div class="flex justify-between items-start">
                <div>
                  <div class="font-medium">{{ getTransactionTypeLabel(transaction.type) }}</div>
                  <div class="text-sm text-theme-text3">{{ formatDate(transaction.date) }}</div>
                  <div *ngIf="transaction.amount" class="text-sm tabular-nums">{{ transaction.amount }} {{ transaction.currency }}</div>
                </div>
                <div class="tabular-nums" [ngClass]="getPointsClass(transaction)">
                  {{ getPointsPrefix(transaction) }}{{ transaction.points }} p
                </div>
              </div>
              <div class="py-3">
                <app-separator *ngIf="!last" [space]="0"></app-separator>
              </div>
            </div>
          </div>

          <!-- Empty state -->
          <div *ngIf="data.transactions.length === 0" class="py-6 text-center text-theme-text3">
            Inga transaktioner hittills.
          </div>
        </div>

        <!-- Error state -->
        <div *ngIf="!isLoading && errorMessage" class="px-8 py-6 text-center">
          <div class="text-red-500 mb-4">{{ errorMessage }}</div>
          <app-base-button baseClass="px-4 py-2 bg-theme-primary text-theme-contrast rounded-lg" (onClick)="loadData()">
            Försök igen
          </app-base-button>
        </div>
      </div>
    </div>
  `
})
export class LoyaltyCardHistoryComponent implements OnInit {
  @Input() data: LoyaltyTransactionResponse | null = null;
  @Input() isLoading: boolean = false;
  errorMessage: string | null = null;
  cardKey?: string;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private apiService: AuthorizedApiService,
    private appState: AppStateService,
    private urlService: UrlHistoryService
  ) {}

  ngOnInit(): void {
    this.appState.observeAppState().pipe(take(1),).subscribe(() => {
      this.loadData();
    });
  }

  loadData(): void {
    this.isLoading = true;
    this.errorMessage = null;

    // Get the card ID from query params if available
    this.route.paramMap.subscribe(params => {
      this.cardKey = params.get('card_key')!;
      this.loadLoyaltyCardHistory(this.cardKey!);
    });
  }

  private loadLoyaltyCardHistory(cardKey: string): void {
    this.apiService.fetchLoyaltyCardHistory(cardKey)
      .pipe(
        catchError(error => {
          console.error('Error loading loyalty card history:', error);
          this.errorMessage = 'Kunde inte ladda lojalitetskortets historik. Försök igen senare.';
          return of(null);
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(response => {
        if (response) {
          this.data = response;
        }
      });
  }

  goBack(): void {
    this.urlService.navigateBack();
  }

  /**
   * Format transaction date to localized string
   */
  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('sv-SE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  }

  /**
   * Get human-readable label for transaction type
   */
  getTransactionTypeLabel(type: string): string {
    const typeMap: Record<string, string> = {
      'earn': 'Intjänad',
      'redeem': 'Inlöst',
      'adjustment': 'Justering',
      'expire': 'Utgången',
      'refund': 'Återbetalning'
    };

    return typeMap[type] || type;
  }

  /**
   * Get CSS class for points display based on transaction type
   */
  getPointsClass(transaction: LoyaltyTransaction): string {
    if (transaction.type === 'earn' || transaction.type === 'refund') {
      return 'font-bold text-theme-notification';
    } else if (transaction.type === 'redeem' || transaction.type === 'expire') {
      return 'font-bold text-theme-notification2';
    }
    return 'font-bold';
  }

  /**
   * Get prefix for points display (+ or -)
   */
  getPointsPrefix(transaction: LoyaltyTransaction): string {
    if (transaction.type === 'earn' || transaction.type === 'refund') {
      return '+';
    }
    // else if (transaction.type === 'redeem' || transaction.type === 'expire') {
    //   return '-';
    // }
    return '';
  }
}
